import {
  Email,
  LocationOn,
  Phone,
  Apartment,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

const DEFAULT_ICP_NUMBER = '粤ICP备16035771号-5';

export const resolveIcpNumber = (configured?: string) =>
  configured?.trim() || DEFAULT_ICP_NUMBER;

interface FooterProps {
  onContact: () => void;
}

const Footer: React.FC<FooterProps> = ({ onContact }) => {
  const icpNumber = resolveIcpNumber(process.env.REACT_APP_ICP_NUMBER);
  const linkGroups = [
    {
      title: '产品',
      links: [
        { label: 'AI 办公', href: '#features' },
        { label: '工作方式', href: '#workflow' },
        { label: '会议辅助', href: '#meeting' },
        { label: '下载客户端', href: '#download' },
      ],
    },
    {
      title: '服务',
      links: [
        { label: '安全治理', href: '#security' },
        { label: '产品交流', href: '#contact', onClick: onContact },
        { label: '公司介绍', href: 'https://www.myqdtg.com/', external: true },
      ],
    },
    {
      title: '合规',
      links: [
        { label: '隐私政策', href: '/legal/privacy.html' },
        { label: '用户协议', href: '/legal/user-agreement.html' },
        { label: '个人信息清单', href: '/legal/userinfo-collect.html' },
        { label: '第三方共享信息清单', href: '/legal/userinfo-share.html' },
      ],
    },
  ];

  const contactInfo = [
    { icon: <Email />, text: 'sales@qdtg.com' },
    { icon: <Phone />, text: '0755-23606709' },
    { icon: <Apartment />, text: '巧夺天宫（深圳）科技有限公司' },
    { icon: <LocationOn />, text: '深圳市龙岗区宝龙街道宝龙社区宝龙四路2号安博科技宝龙厂区2号厂房1707' },
  ];

  return (
    <Box
      id="footer"
      component="footer"
      sx={{
        backgroundColor: 'rgb(var(--footer-rgb))',
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 5, md: 8 },
            mb: 5,
          }}
        >
          {/* 品牌 + 联系 */}
          <Box sx={{ flex: { xs: 'none', md: '1.2' }, minWidth: { xs: '100%', md: '300px' } }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <img
                src="/logo192.png"
                alt="飞舟"
                style={{ width: 32, height: 32, borderRadius: 8, display: 'block' }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                飞舟 Agent 办公
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.55)',
                lineHeight: 1.8,
                mb: 3,
                maxWidth: 360,
              }}
            >
              面向工程企业的 Agent 办公平台。让 AI 理解业务上下文、调用办公工具，
              把日常事务推进到可复核、可执行的结果。
            </Typography>
            <Stack spacing={1}>
              {contactInfo.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ color: 'rgba(255, 255, 255, 0.4)', mt: 0.25, fontSize: 18, display: 'flex' }}>
                    {item.icon}
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.55)', lineHeight: 1.7 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* 链接组 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 4, sm: 3, md: 6 },
              flex: 2,
            }}
          >
            {linkGroups.map((group, index) => (
              <Box key={index} sx={{ flex: 1, minWidth: '140px' }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}
                >
                  {group.title}
                </Typography>
                <Stack spacing={1.25}>
                  {group.links.map((link, linkIndex) => (
                    <Link
                      key={linkIndex}
                      href={link.href}
                      onClick={
                        link.onClick
                          ? (event) => {
                              event.preventDefault();
                              link.onClick?.();
                            }
                          : undefined
                      }
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.55)',
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        width: 'fit-content',
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          color: 'white',
                        },
                        transition: 'color .2s',
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            © 2026 巧夺天宫（深圳）科技有限公司。保留所有权利。
          </Typography>
          <Link
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${icpNumber}，前往工信部备案管理系统`}
            sx={{
              color: 'rgba(255, 255, 255, 0.62)',
              textDecoration: 'none',
              fontSize: '0.875rem',
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              '&:hover': { color: 'white', textDecoration: 'underline' },
            }}
          >
            {icpNumber}
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
